<template>
    <div>
        <button
            :name="`edit_address_${address.id}_${field}`"
            :class="[
                field === 'email' ? 'break-all' : '',
                'font-semibold text-nutreance-green text-left hover:text-nutreance-green-lighter focus:outline-none',
            ]"
            @click="openModal"
        >
            {{ address[field] ? address[field] : "(none)" }}
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                @close="open = false"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                        >
                            <div
                                class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                            >
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="open = false"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <PencilAltIcon
                                        class="h-6 w-6 text-nutreance-green"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="mt-3 sm:mt-0 w-full sm:mr-10">
                                    <DialogTitle
                                        as="h3"
                                        class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                    >
                                        Edit {{ ucwords(field) }}
                                    </DialogTitle>
                                </div>
                            </div>

                            <form
                                ref="form"
                                :action="actionUrl"
                                method="POST"
                                @submit.prevent="onSubmit(form)"
                            >
                                <div
                                    class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                >
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="csrf"
                                    />
                                    <input
                                        type="hidden"
                                        name="_method"
                                        value="PATCH"
                                    />
                                    <input
                                        type="hidden"
                                        name="id"
                                        :value="inputs.id"
                                    />

                                    <div class="sm:col-span-2 sm:px-16 pb-6">
                                        <div class="py-2">
                                            <label
                                                :for="field"
                                                class="block text-sm font-medium text-gray-700"
                                            >
                                                {{ ucwords(field) }}
                                            </label>

                                            <div
                                                class="mt-1 relative rounded-md shadow-sm"
                                            >
                                                <input
                                                    v-model="inputs.field"
                                                    :ref="refs.field"
                                                    :name="field"
                                                    :id="field"
                                                    :type="
                                                        field === 'phone'
                                                            ? 'tel'
                                                            : field
                                                    "
                                                    :autocomplete="field"
                                                    placeholder="(optional)"
                                                    :class="[
                                                        hasError(errors, field)
                                                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                        'appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                                    ]"
                                                    :aria-invalid="
                                                        hasError(errors, field)
                                                            ? 'true'
                                                            : 'false'
                                                    "
                                                    :aria-describedby="
                                                        hasError(errors, field)
                                                            ? field + '-error'
                                                            : ''
                                                    "
                                                />
                                                <div
                                                    v-if="
                                                        hasError(errors, field)
                                                    "
                                                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                                >
                                                    <ExclamationCircleIcon
                                                        class="h-5 w-5 text-red-500"
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                            </div>
                                            <p
                                                v-if="hasError(errors, field)"
                                                class="mt-2 text-sm text-red-600"
                                                :id="field + '-error'"
                                            >
                                                {{ error(errors, field) }}
                                            </p>
                                        </div>

                                        <div
                                            v-show="
                                                field === 'email' &&
                                                inputs.field
                                            "
                                            class="py-2 sm:pb-0"
                                        >
                                            <div
                                                class="relative flex items-start"
                                            >
                                                <div
                                                    class="flex items-center h-5"
                                                >
                                                    <input
                                                        v-model="
                                                            inputs.email_opt_in
                                                        "
                                                        id="email_opt_in"
                                                        name="email_opt_in"
                                                        type="checkbox"
                                                        class="focus:ring-transparent h-4 w-4 text-nutreance-green border-gray-300 rounded"
                                                    />
                                                </div>
                                                <div class="ml-3 text-sm">
                                                    <label
                                                        for="email_opt_in"
                                                        class="text-gray-900"
                                                        >I would like to receive
                                                        emails about coupons,
                                                        discounts, and related
                                                        products.</label
                                                    >
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        name="yes"
                                        type="submit"
                                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green text-base font-medium text-white hover:bg-nutreance-green-darker focus:outline-none sm:col-start-2 sm:text-sm"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-5 w-5 mr-2"
                                        />
                                        <CheckIcon
                                            v-else
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Save
                                    </button>

                                    <button
                                        name="no"
                                        type="button"
                                        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                        @click="open = false"
                                    >
                                        <XIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { onMounted, nextTick, reactive, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    CheckIcon,
    ExclamationCircleIcon,
    PencilAltIcon,
    XIcon,
} from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import LoadingIcon from "../LoadingIcon.vue";
import { useString } from "../../functions/useString";

const props = withDefaults(
    defineProps<{
        address?: any;
        field: string;
        csrf?: string;
        emailOptIn?: string;
        errors?: any;
        old?: any;
    }>(),
    {
        emailOptIn: "",
    },
);

const { error, hasError, oldInput, onSubmit, submitted } = useForm();
const { ucwords } = useString();
const form = ref<any>(null);
const actionUrl = "/user/addresses/" + props.address.id + "/" + props.field;
const open = ref(
    Object.prototype.hasOwnProperty.call(props.old, props.field) &&
        Object.prototype.hasOwnProperty.call(props.old, "id") &&
        parseInt(props.old.id) === props.address.id,
);

const refs = {
    field: ref<any>(null),
};

const inputs = reactive<{ id: any; field: any; email_opt_in: boolean }>({
    id: props.address.id,
    field: oldInput(props.old, props.field)
        ? oldInput(props.old, props.field)
        : props.address[props.field],
    email_opt_in: !!(Object.prototype.hasOwnProperty.call(
        props.old,
        "email_opt_in",
    )
        ? oldInput(props.old, "email_opt_in")
        : props.emailOptIn),
});

function openModal() {
    open.value = true;

    nextTick(() => refs.field.value.focus());
}

onMounted(() => {
    if (open.value) {
        openModal();
    }
});
</script>
